/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import React, { useMemo, useState } from 'react';
import { useGetExportTemplates } from '@utils/api/organizations/use-get-export-templates.tsx';
import toast from 'react-hot-toast';
import { getDocumentRaw } from '@utils/api/documents/use-get-document-raw.tsx';
import {
  getDocumentDirectUrl,
  triggerDownloadFromUrl
} from '@utils/api/documents/use-get-document-direct.tsx';
import { parse } from '@utils/extensions/content-disposition.extension.ts';
import saveAs from 'file-saver';
import { WorkspaceRow } from '@components/main-workspace/workspace.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import {
  FileCode,
  FileDown,
  FileText,
  FileType2,
  Mic,
  UploadCloud,
  ExternalLink
} from 'lucide-react';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import { useSwallowDocumentFinished } from '@utils/helpers/document-finished.ts';
import { Link, useNavigate } from 'react-router-dom';
import PublishDialog from '@components/publishing/publish-dialog/publish-dialog.tsx';
import { CONNECTIONS_TAB_ID } from '@components/publishing/profile-connections-tab-id.ts';
import { useIsPublishingEnabled } from '@utils/api/connections/use-is-publishing-enabled.tsx';
import { Send } from 'lucide-react';
import { PATHS } from '../../router/paths.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { DocumentTagSummary } from '@components/workspace/document-tag-summary.tsx';

type Props = {
  modalTranscript: boolean;
  setModalTranscript: React.Dispatch<React.SetStateAction<boolean>>;
  selectedItem: WorkspaceRow | undefined;
  handleMediaDownload: () => void;
  includeTimestamps: boolean;
  setIncludeTimestamps: React.Dispatch<React.SetStateAction<boolean>>;
  includeSpeakers: boolean;
  setIncludeSpeakers: React.Dispatch<React.SetStateAction<boolean>>;
  sessionId: string;
  documentId: string;
  /** When true, this form is used in a shared (magic link) context */
  isShared?: boolean;
  /** Token for shared document authentication */
  token?: string;
};

const ExportDocumentForm = ({
  modalTranscript,
  setModalTranscript,
  selectedItem,
  handleMediaDownload,
  includeTimestamps,
  setIncludeTimestamps,
  includeSpeakers,
  setIncludeSpeakers,
  sessionId,
  documentId,
  isShared = false,
  token
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { isEnabled: isPublishingEnabled } = useIsPublishingEnabled(!isShared);
  const navigate = useNavigate();
  const [publishOpen, setPublishOpen] = useState(false);
  const canPublish = !isShared && isPublishingEnabled;

  // Prevent error toasts for handled fallback scenarios
  useSwallowDocumentFinished();

  // Only fetch org export templates when NOT in shared mode
  const { data: templates, isLoading } = useGetExportTemplates({
    enabled: modalTranscript && !isShared
  });

  const [exportingTemplateId, setExportingTemplateId] = useState<number | null>(
    null
  );
  const [sendTemplateToEmail, setSendTemplateToEmail] = useState(false);

  const handleExportTranscript = async (
    format: string,
    templateId?: number,
    sendToEmail?: boolean
  ) => {
    if (!selectedItem) {
      toast.error(t('error-downloading-document'));
      return;
    }

    const toastId = toast.loading(t('exporting-in-progress'));

    if (format === 'template' && templateId != null) {
      setExportingTemplateId(templateId);
    }

    try {
      // Formats that typically have pre-generated files and support direct download
      const supportsDirectDownload = ['docx', 'pdf', 'template'].includes(
        format
      );

      // For formats with pre-generated files, try direct download first
      // For other formats (txt, html, etc.), skip directly to streaming to avoid extra round trip
      if (supportsDirectDownload) {
        try {
          const response = await getDocumentDirectUrl({
            sessionId: sessionId,
            documentId: documentId,
            format: format,
            templateId: templateId?.toString(),
            without_timestamps: !includeTimestamps,
            without_speakers: !includeSpeakers,
            token,
            isShared,
            // Only pass send_to_email for template exports
            ...(format === 'template' &&
              sendToEmail !== undefined && { send_to_email: sendToEmail })
          });

          toast.dismiss(toastId);

          // Trigger download directly from Azure Blobs (bypasses load balancer)
          await triggerDownloadFromUrl(
            response.result.download_url,
            response.result.filename,
            response.result.content_type
          );
          toast.success(t('download-complete'));
          setModalTranscript(false);
          return; // Success, exit early
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (directError: any) {
          // If direct download not supported, fall through to streaming method
          if (
            directError?.response?.status !== 400 ||
            directError?.response?.data?.error !==
              'direct-download-not-supported'
          ) {
            // Re-throw non-fallback errors
            throw directError;
          }
          // Otherwise continue to streaming fallback below
        }
      }

      // Use streaming method for formats without pre-generated files
      // or as fallback when direct download not supported
      const response = await getDocumentRaw({
        sessionId: sessionId,
        documentId: documentId,
        format: format,
        templateId: templateId?.toString(),
        without_timestamps: !includeTimestamps,
        without_speakers: !includeSpeakers,
        token,
        isShared,
        // Only pass send_to_email for template exports
        ...(format === 'template' &&
          sendToEmail !== undefined && { send_to_email: sendToEmail })
      });

      toast.dismiss(toastId);

      if (response.status === 204) {
        toast.success(
          sendToEmail
            ? t(
                'the-document-will-be-sent-to-your-email-and-available-in-export-history'
              )
            : t('the-document-will-be-available-in-export-history')
        );
        setModalTranscript(false);
      } else {
        const disposition = parse(
          response.headers['content-disposition'] || ''
        );
        const ext = format === 'template' ? 'docx' : format;
        const filename = disposition?.parameters?.filename || `download.${ext}`;
        saveAs(response.data, filename);
        toast.success(t('download-complete'));
        setModalTranscript(false);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.dismiss(toastId);

      // Handle errors
      if (error.response?.status === 404) {
        toast.error(t('document-not-found'));
      } else if (error.response?.status === 409) {
        toast.error(t('no-claims-available-for-stt-templates'));
      } else {
        toast.error(t('error-downloading-document'));
      }
    } finally {
      setExportingTemplateId(null);
    }
  };

  const exportItemList = useMemo(() => {
    const basicItems = [
      {
        icon: FileText,
        label: t('Docx'),
        description:
          t('export-as-editable-docx') || 'Export as an editable DOCX file.',
        onClick: () => handleExportTranscript('docx'),
        disabled: false
      },
      {
        icon: Mic,
        label: t('media-file'),
        description:
          t('download-original-media') || 'Download the original media file.',
        onClick: () => handleMediaDownload(),
        disabled: false
      },
      {
        icon: FileType2,
        label: t('text-file'),
        description:
          t('simple-text-version') ||
          'Plain text (.txt) version of transcript.',
        onClick: () => handleExportTranscript('txt'),
        disabled: false
      },
      {
        icon: FileCode,
        label: t('html-doc'),
        description:
          t('styled-html-export') || 'HTML export for web or styling.',
        onClick: () => handleExportTranscript('html'),
        disabled: false
      },
      {
        icon: FileDown,
        label: t('pdf-doc'),
        description:
          t('ready-to-share-pdf') || 'PDF export ready to share or print.',
        onClick: () => handleExportTranscript('pdf'),
        disabled: false
      }
    ];

    const templatesItems: {
      id: number;
      icon: typeof UploadCloud;
      label: string;
      description: string;
      onClick: () => void;
      disabled: boolean;
    }[] = [];

    if (
      !isShared &&
      templates &&
      templates.result.length > 0 &&
      templates.total_results > 0
    ) {
      for (const template of templates.result) {
        if (template.id == null) continue;

        templatesItems.push({
          id: template.id,
          icon: UploadCloud,
          label: template.name,
          description:
            template.type === 'SUMMARIZATION'
              ? t('summarize-using-template')
              : t('export-using-template') ||
                'Use your custom DOCX/PDF export template.',
          onClick: () =>
            handleExportTranscript(
              'template',
              template.id,
              sendTemplateToEmail
            ),
          disabled: false
        });
      }
    }

    return { basicItems, templatesItems };
  }, [
    t,
    handleMediaDownload,
    templates,
    isShared,
    handleExportTranscript,
    sendTemplateToEmail
  ]);

  return (
    <Modal
      isOpen={modalTranscript}
      onClose={() => setModalTranscript(false)}
      size="medium"
      title={t('download-document')}
      caption={t(
        'using-the-settings-below-define-format-the-structure-and-format-of-the-file-you-are-about-to-download'
      )}
      footerActions={[
        ...(canPublish
          ? [
              <Button
                key="publish"
                onClick={() => setPublishOpen(true)}
                label={t('publish-cta')}
                size="small"
                variant="primary"
                icon={<Send size={14} aria-hidden="true" />}
              />
            ]
          : []),
        <Button
          key="cancel"
          onClick={() => setModalTranscript(false)}
          label={t('cancel')}
          size="small"
          variant="smoke"
        />
      ]}
    >
      <div className="space-y-6">
        {/* CONTENT OPTIONS */}
        <section className="px-4 py-2">
          <div className="flex flex-col gap-1">
            <div>
              <h3
                className="text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('content-options') || 'Content options'}
              </h3>
              <p
                className="mt-0.5 text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('choose-what-to-include') ||
                  'Choose whether speakers and timestamps should be included in your export.'}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between py-1">
                <Checkbox
                  id="include_speakers"
                  name="include_speakers"
                  label={t('include-speakers')}
                  checked={includeSpeakers}
                  onChange={e => setIncludeSpeakers(e.target.checked)}
                  variant="primary"
                  wrapperClass="w-full flex items-center justify-between cursor-pointer select-none"
                  labelClass="text-sm font-medium"
                />
              </div>

              <div className="flex items-center justify-between py-1">
                <Checkbox
                  id="include_timestamps"
                  name="include_timestamps"
                  label={t('include-timestamps')}
                  checked={includeTimestamps}
                  onChange={e => setIncludeTimestamps(e.target.checked)}
                  variant="primary"
                  wrapperClass="w-full flex items-center justify-between cursor-pointer select-none"
                  labelClass="text-sm font-medium"
                />
              </div>
            </div>
          </div>
        </section>

        {/* BASIC EXPORT OPTIONS */}
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3
              className="text-sm font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {t('export-options') || 'Export options'}
            </h3>
            <p className="text-xs" style={{ color: getColour('inactiveText') }}>
              {t('pick-your-preferred-format') ||
                'Pick your preferred format for this transcript.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {exportItemList.basicItems.map(option => (
              <button
                key={option.label as string}
                type="button"
                onClick={option.onClick}
                disabled={option.disabled}
                className="flex h-full w-full items-start gap-3 rounded-2xl border px-3 py-2.5 text-left text-sm shadow-sm hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-60"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/5 text-primary">
                  <option.icon className="h-4 w-4" />
                </span>
                <span className="flex flex-col gap-0.5">
                  <span
                    className="font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {option.label}
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {option.description}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* TEMPLATE EXPORT OPTIONS (only when not shared) */}
        {!isShared && (
          <section
            className="space-y-3 border-t pt-4"
            style={{ borderColor: getColour('border') }}
          >
            <div className="flex items-center justify-between gap-2">
              <h3
                className="text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('export-using-templates') || 'Export using templates'}
              </h3>
              <p
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('use-custom-docx-templates')}
              </p>
            </div>

            {isLoading ? (
              <div className="flex w-full items-center justify-center py-4">
                <Loader height={10} width={3} />
              </div>
            ) : exportItemList.templatesItems.length > 0 ? (
              <>
                {/* Tag summary for template exports */}
                {!isShared && (
                  <DocumentTagSummary
                    sessionId={sessionId}
                    documentId={documentId}
                  />
                )}

                <div
                  className="rounded-2xl border px-4 py-3"
                  style={{
                    borderColor: getColour('border'),
                    backgroundColor: getColour('thirdLayer')
                  }}
                >
                  <Checkbox
                    id="send_template_to_email"
                    name="send_template_to_email"
                    label={t('send-to-email')}
                    checked={sendTemplateToEmail}
                    onChange={e => setSendTemplateToEmail(e.target.checked)}
                    variant="primary"
                    wrapperClass="w-full flex items-center justify-between cursor-pointer select-none"
                    labelClass="text-sm font-medium"
                  />
                  <p
                    className="mt-1 text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('send-template-export-to-email-description') ||
                      'When enabled, the export will be sent to your email and saved to export history.'}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {exportItemList.templatesItems.map(option => {
                    const isTemplateLoading = option.id === exportingTemplateId;

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          if (!isTemplateLoading) {
                            option.onClick();
                          }
                        }}
                        disabled={isTemplateLoading}
                        className="flex h-full w-full items-start gap-3 rounded-2xl border border-dashed px-3 py-2.5 text-left text-sm hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-70"
                        style={{
                          borderColor: getColour('border'),
                          backgroundColor: getColour('thirdLayer')
                        }}
                      >
                        <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/5 text-primary">
                          <option.icon className="h-4 w-4" />
                        </span>
                        <span className="flex flex-col gap-0.5 min-w-0">
                          <span
                            className="truncate text-sm font-medium"
                            style={{ color: getColour('normalText') }}
                          >
                            {isTemplateLoading
                              ? `${t('exporting') || 'Exporting'}…`
                              : option.label}
                          </span>
                          <span
                            className="text-xs"
                            style={{ color: getColour('inactiveText') }}
                          >
                            {option.description}
                          </span>
                          {isTemplateLoading && (
                            <span
                              className="mt-1 inline-flex items-center gap-1 text-[11px]"
                              style={{ color: getColour('inactiveText') }}
                            >
                              <Loader height={8} width={2} />
                              <span>{t('please-wait') || 'Please wait…'}</span>
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <p
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('no-templates-found') ||
                  'No export templates are configured yet. You can still export using the basic formats above.'}
              </p>
            )}
          </section>
        )}

        {/* EXPORT HISTORY LINK */}
        {!isShared && (
          <div
            className="pt-4 border-t"
            style={{ borderColor: getColour('border') }}
          >
            <Link
              to={PATHS().EXPORT_TEMPLATE_ITEMS}
              className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 hover:underline"
              onClick={() => setModalTranscript(false)}
            >
              <ExternalLink className="h-4 w-4" />
              {t('view-export-history')}
            </Link>
          </div>
        )}
      </div>
      {canPublish && sessionId && documentId && (
        <PublishDialog
          isOpen={publishOpen}
          onClose={() => setPublishOpen(false)}
          sessionId={sessionId}
          documentId={documentId}
          defaultPayloadKind="transcript"
          allowKindToggle
          availableCaptionLanguages={
            selectedItem?.language ? [selectedItem.language] : []
          }
          transcriptTemplates={(templates?.result ?? [])
            .filter(tpl => tpl?.id != null && tpl?.name)
            .map(tpl => ({ id: String(tpl.id), label: tpl.name as string }))}
          onOpenConnectWizard={() => {
            setPublishOpen(false);
            navigate(`${PATHS().PROFILE}?tab=${CONNECTIONS_TAB_ID}`);
          }}
        />
      )}
    </Modal>
  );
};

export default ExportDocumentForm;
